import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useEditor } from '../../contexts/EditorContext';
import { useAuth } from '../../contexts/AuthContext';
import UpgradeModal from '../common/UpgradeModal';
import { 
  Type, 
  Square, 
  Circle,
  Image as ImageIcon,
  Undo, 
  Redo, 
  Download,
  Save,
  Trash2,
  Copy,
  ArrowLeft,
  Lock
} from 'lucide-react';

const EditorToolbar = ({ onSave, onExport }) => {
  const navigate = useNavigate();
  const { isPaidUser } = useAuth();
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const {
    addElement,
    undo,
    redo,
    deleteSelected,
    duplicateElement,
    selectedElements,
    history,
    historyStep,
    carouselId,
    carouselData,
  } = useEditor();
  
  const fileInputRef = useRef(null);
  
  const handleAddText = () => {
    addElement({
      id: `text-${Date.now()}`,
      type: 'text',
      x: 100,
      y: 100,
      text: 'Double-click to edit',
      fontSize: 32,
      fontFamily: 'Arial',
      fill: '#000000',
      width: 300,
    });
  };
  
  const handleAddRect = () => {
    addElement({
      id: `rect-${Date.now()}`,
      type: 'rect',
      x: 100,
      y: 100,
      width: 200,
      height: 150,
      fill: '#3B82F6',
      stroke: '#000000',
      strokeWidth: 0,
      cornerRadius: 0,
    });
  };
  
  const handleAddCircle = () => {
    addElement({
      id: `circle-${Date.now()}`,
      type: 'circle',
      x: 200,
      y: 200,
      radius: 75,
      fill: '#EF4444',
      stroke: '#000000',
      strokeWidth: 0,
    });
  };
  
  const handleImageUpload = () => {
    fileInputRef.current?.click();
  };
  
  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Block all image uploads for free users
    if (!isPaidUser) {
      setShowUpgradeModal(true);
      e.target.value = '';
      return;
    }
    
    // Check if file is an image
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }
    
    // Read file as base64
    const reader = new FileReader();
    reader.onload = (event) => {
      const imageUrl = event.target.result;
      
      // Create image element to get dimensions
      const img = new window.Image();
      img.onload = () => {
        // Add image element to canvas at 60% of original size
        const scaleFactor = 0.6;
        addElement({
          id: `image-${Date.now()}`,
          type: 'image',
          x: 100,
          y: 100,
          width: img.width * scaleFactor,
          height: img.height * scaleFactor,
          src: imageUrl, // Base64 data URL
          rotation: 0,
        });
      };
      img.src = imageUrl;
    };
    reader.readAsDataURL(file);
    
    // Reset input
    e.target.value = '';
  };
  
  const canUndo = historyStep > 0;
  const canRedo = historyStep < history.length - 1;
  
  return (
    <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center gap-2 shadow-sm">
      {/* Back Button */}
      {carouselId && (
        <button
          onClick={() => navigate(`/carousel/${carouselId}`)}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors text-gray-600 hover:text-gray-900 flex items-center gap-1 text-sm mr-1"
          title="Back to carousel"
        >
          <ArrowLeft size={18} />
          <span className="font-medium max-w-32 truncate">{carouselData?.title || 'Back'}</span>
        </button>
      )}

      {/* Add Elements */}
      <div className="flex items-center gap-1 border-r border-gray-300 pr-3 mr-2">
        <button
          onClick={handleAddText}
          className="p-2 hover:bg-blue-50 rounded-lg transition-colors flex items-center gap-2 text-gray-700 hover:text-blue-600"
          title="Add Text (T)"
        >
          <Type size={20} />
          <span className="text-sm font-medium">Text</span>
        </button>
        <button
          onClick={handleAddRect}
          className="p-2 hover:bg-blue-50 rounded-lg transition-colors flex items-center gap-2 text-gray-700 hover:text-blue-600"
          title="Add Rectangle (R)"
        >
          <Square size={20} />
          <span className="text-sm font-medium">Rectangle</span>
        </button>
        <button
          onClick={handleAddCircle}
          className="p-2 hover:bg-blue-50 rounded-lg transition-colors flex items-center gap-2 text-gray-700 hover:text-blue-600"
          title="Add Circle (C)"
        >
          <Circle size={20} />
          <span className="text-sm font-medium">Circle</span>
        </button>
        <button
          onClick={handleImageUpload}
          className={`p-2 rounded-lg transition-colors flex items-center gap-2 ${
            isPaidUser 
              ? 'hover:bg-blue-50 text-gray-700 hover:text-blue-600' 
              : 'bg-gray-100 text-gray-400 hover:bg-amber-50 hover:text-amber-600 cursor-pointer'
          }`}
          title={isPaidUser ? "Add Image (I)" : "Add Image (Paid users only)"}
        >
          {!isPaidUser && <Lock size={14} className="text-amber-500" />}
          <ImageIcon size={20} />
          <span className="text-sm font-medium">Image</span>
        </button>
        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          className="hidden"
        />
      </div>
      
      {/* Undo/Redo */}
      <div className="flex items-center gap-1 border-r border-gray-300 pr-3 mr-2">
        <button
          onClick={undo}
          disabled={!canUndo}
          className={`p-2 rounded-lg transition-colors ${
            canUndo 
              ? 'hover:bg-gray-100 text-gray-700' 
              : 'text-gray-300 cursor-not-allowed'
          }`}
          title="Undo (Ctrl+Z)"
        >
          <Undo size={20} />
        </button>
        <button
          onClick={redo}
          disabled={!canRedo}
          className={`p-2 rounded-lg transition-colors ${
            canRedo 
              ? 'hover:bg-gray-100 text-gray-700' 
              : 'text-gray-300 cursor-not-allowed'
          }`}
          title="Redo (Ctrl+Y)"
        >
          <Redo size={20} />
        </button>
      </div>
      
      {/* Duplicate / Delete (visible when element selected) */}
      {selectedElements.length > 0 && (
        <div className="flex items-center gap-1 border-r border-gray-300 pr-3 mr-2">
          <button
            onClick={duplicateElement}
            className="p-2 hover:bg-blue-50 rounded-lg transition-colors text-gray-700 hover:text-blue-600 flex items-center gap-1"
            title="Duplicate (Ctrl+D)"
          >
            <Copy size={20} />
            <span className="text-sm font-medium">Duplicate</span>
          </button>
          <button
            onClick={deleteSelected}
            className="p-2 hover:bg-red-50 rounded-lg transition-colors text-red-600 hover:text-red-700"
            title="Delete (Delete)"
          >
            <Trash2 size={20} />
          </button>
        </div>
      )}
      
      {/* Save/Export */}
      <div className="flex items-center gap-2 ml-auto">
        <button
          onClick={onSave}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 font-medium shadow-sm"
        >
          <Save size={16} />
          Save
        </button>
        <button
          onClick={onExport}
          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2 font-medium shadow-sm"
        >
          <Download size={16} />
          Export
        </button>
      </div>

      {/* Upgrade Modal */}
      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        feature="Custom image uploads"
      />
    </div>
  );
};

export default EditorToolbar;
